/**
 * 自社の食材・部品（試用・2026-10-07）— `/ingredients`。
 *
 * - **食材**: 成分表に無い市販品（業務用ソース・冷凍食品など）を、規格書の 100g あたりの値で登録する
 * - **部品**: ごはん（並）・タルタルソース・ドレッシングなど、自社のレシピから作る部品。材料と重量から 100g あたりを計算して保存する
 * 登録したものは「材料から計算」の材料として使える（★）。定食・セット・サイズ違いは、部品を組み合わせて作る。
 * ⚠️ 部品の値は保存した時点の計算。中の材料を直したら保存し直し、使っているメニューも計算し直す（使っている先を画面に出す）。
 * ⚠️ 配合と仕入先は御社の機密 — kalori のカタログには出ない。
 */
import { useEffect, useState } from "react";
import { api, type AllergenState, type IngredientHit, type OwnIngredient, type RecipeCalc, type RecipeKey } from "../api";
import { Link } from "../router";
import { NUTRITION_FIELDS, REVIEW_STATE_LABEL, fmtNumber } from "../format";
import { AllergenEditor } from "./AllergenEditor";

type Form = Record<RecipeKey, string>;
const emptyForm = (): Form => ({ cal: "", protein: "", fat: "", carbs: "", sugar: "", fiber: "", salt: "" });

function toNum(raw: string): number | null | undefined {
  const s = raw.normalize("NFKC").replace(/[,\s]/g, "");
  if (s === "") return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return undefined;
  return Number(s);
}

interface Draft {
  id: string | null;
  kind: "spec" | "part";
  name: string;
  maker: string;
  servingGrams: string;
  per100: Form;
  rows: { id: string; name: string; grams: string }[];
  finished: string;
  allergens: Record<string, AllergenState>;
  basisNote: string;
}

function toDraft(o: OwnIngredient | null, kind: "spec" | "part"): Draft {
  const per100 = emptyForm();
  if (o) for (const f of NUTRITION_FIELDS) per100[f.key] = o.per100g[f.key] == null ? "" : String(o.per100g[f.key]);
  return {
    id: o?.id ?? null,
    kind: o?.kind ?? kind,
    name: o?.name ?? "",
    maker: o?.maker ?? "",
    servingGrams: o?.servingGrams == null ? "" : String(o.servingGrams),
    per100,
    rows: o?.recipe?.items.map((i) => ({ id: i.id, name: i.id, grams: String(i.grams) })) ?? [],
    finished: o?.recipe?.finishedGrams ? String(o.recipe.finishedGrams) : "",
    allergens: o?.allergenStates?.items ?? {},
    basisNote: o?.basisNote ?? "",
  };
}

export function MyIngredients() {
  const [list, setList] = useState<OwnIngredient[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => void api.myIngredients().then((r) => (r.ok ? setList(r.data) : setError(r.error)));
  useEffect(load, []);

  return (
    <section className="stack-lg">
      <div className="row-between">
        <div>
          <h1>
            自社の食材・部品 <span className="badge trial">試用</span>
          </h1>
          <p className="muted">
            成分表に無い市販品や、自社で作る部品（ごはん・ソースなど）を登録しておくと、「材料から計算」で使えます。配合と仕入先は御社の機密として扱い、kalori のカタログには出しません。
          </p>
        </div>
        {!draft && (
          <div className="actions">
            <button className="btn" onClick={() => setDraft(toDraft(null, "spec"))}>
              食材を登録（規格書の値）
            </button>
            <button className="btn primary" onClick={() => setDraft(toDraft(null, "part"))}>
              部品を作る（自社のレシピ）
            </button>
          </div>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      {draft ? (
        <IngredientEditor
          draft={draft}
          onClose={(changed) => {
            setDraft(null);
            if (changed) load();
          }}
        />
      ) : (
        <div className="card flush">
          {list === null && <p className="muted card-title">読み込んでいます…</p>}
          {list && list.length === 0 && <p className="muted card-title">まだ登録はありません。</p>}
          {list && list.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>種類</th>
                  <th className="num">100g あたり</th>
                  <th className="num hide-sm">1 回分</th>
                </tr>
              </thead>
              <tbody>
                {list.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => setDraft(toDraft(o, o.kind))}>
                    <td>
                      <button type="button" className="btn-link row-title">
                        {o.name}
                      </button>
                      {o.maker && <div className="muted small">{o.maker}</div>}
                    </td>
                    <td>{o.kind === "part" ? "部品" : "食材"}</td>
                    <td className="num">{fmtNumber(o.per100g.cal, "kcal")}</td>
                    <td className="num hide-sm">{fmtNumber(o.servingGrams, "g")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}

function IngredientEditor({ draft: initial, onClose }: { draft: Draft; onClose: (changed: boolean) => void }) {
  const [d, setD] = useState<Draft>(initial);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<IngredientHit[]>([]);
  const [calc, setCalc] = useState<RecipeCalc | null>(null);
  const [usage, setUsage] = useState<{ entries: { id: string; name: string; reviewState: string }[]; parts: { id: string; name: string }[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<Draft>) => setD({ ...d, ...patch });

  useEffect(() => {
    if (initial.id) void api.myIngredientUsage(initial.id).then((r) => r.ok && setUsage(r.data));
  }, [initial.id]);

  // 部品: 材料名を引き直し、100g あたりの計算を出す（行・仕上がり重量が変わって止まったら 1 回）
  const items = d.rows.map((r) => ({ id: r.id, grams: toNum(r.grams) }));
  const finishedN = d.finished.trim() ? toNum(d.finished) : null;
  const key = JSON.stringify([items, finishedN]);
  useEffect(() => {
    if (d.kind !== "part" || items.length === 0 || items.some((i) => i.grams == null)) return setCalc(null);
    const t = setTimeout(() => {
      void api
        .recipeCalc({ name: d.name, servings: 1, items: items.map((i) => ({ id: i.id, grams: i.grams! })), finishedGrams: finishedN ?? null })
        .then((r) => {
          if (!r.ok) return;
          setCalc(r.data);
          // 保存済みの部品を開いたときは行の名前が ID のままなので、計算の応答で埋める
          if (d.rows.some((row, i) => row.name === row.id && r.data.items[i])) {
            setD((cur) => ({ ...cur, rows: cur.rows.map((row, i) => ({ ...row, name: r.data.items[i]?.name ?? row.name })) }));
          }
        });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, d.kind]);

  useEffect(() => {
    const q = query.trim();
    if (!q) return setHits([]);
    const t = setTimeout(() => void api.searchIngredients(q).then((r) => r.ok && setHits(r.data.filter((h) => h.id !== d.id))), 250);
    return () => clearTimeout(t);
  }, [query, d.id]);

  const save = async () => {
    setError(null);
    if (!d.name.trim()) return setError("名前を入れてください。");
    const sg = d.servingGrams.trim() ? toNum(d.servingGrams) : null;
    if (sg === undefined) return setError("1 回分の重量は g の数字で入れてください。");
    const common = {
      name: d.name.trim(),
      servingGrams: sg,
      allergenStates: Object.keys(d.allergens).length > 0 ? { items: d.allergens } : null,
      basisNote: d.basisNote.trim() || null,
    };
    let body: Record<string, unknown>;
    if (d.kind === "spec") {
      const per100g = {} as Record<RecipeKey, number | null>;
      for (const f of NUTRITION_FIELDS) {
        const v = toNum(d.per100[f.key]);
        if (v === undefined) return setError(`${f.label} は数字で入れてください。`);
        per100g[f.key] = v;
      }
      body = { kind: "spec", maker: d.maker.trim() || null, per100g, ...common };
    } else {
      if (items.length === 0 || items.some((i) => i.grams == null)) return setError("材料と重量を入れてください。");
      if (finishedN === undefined) return setError("仕上がり重量は g の数字で入れてください。");
      body = { kind: "part", recipe: { items: items.map((i) => ({ id: i.id, grams: i.grams! })), finishedGrams: finishedN }, ...common };
    }
    setBusy(true);
    const r = await api.saveMyIngredient(d.id, body);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    onClose(true);
  };

  const remove = async () => {
    if (!d.id) return;
    setBusy(true);
    const r = await api.deleteMyIngredient(d.id);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    onClose(true);
  };

  return (
    <div className="stack-lg">
      <div className="card stack">
        <div className="row-between">
          <h2>{d.id ? "編集" : d.kind === "part" ? "部品を作る" : "食材を登録"}</h2>
          <button type="button" className="btn-link" onClick={() => onClose(false)}>
            一覧へ戻る
          </button>
        </div>
        <label className="field">
          <span>名前</span>
          <input value={d.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} placeholder={d.kind === "part" ? "例: ごはん（並）・自家製タルタル" : "例: 業務用タルタルソース"} />
        </label>
        {d.kind === "spec" && (
          <label className="field">
            <span>製造者・仕入先（任意）</span>
            <input value={d.maker} maxLength={120} onChange={(e) => set({ maker: e.target.value })} />
          </label>
        )}
        <label className="field inline-field">
          <span>1 回に使う量（任意）</span>
          <span className="input-unit">
            <input inputMode="decimal" value={d.servingGrams} onChange={(e) => set({ servingGrams: e.target.value })} aria-label="1 回に使う量" />
            <span className="unit">g</span>
          </span>
          <span className="muted small">「材料から計算」で追加したときの重量の初期値</span>
        </label>

        {d.kind === "spec" ? (
          <table className="table form-table">
            <caption className="small">100g あたり（規格書の値・分からない項目は空欄）</caption>
            <tbody>
              {NUTRITION_FIELDS.map((f) => (
                <tr key={f.key}>
                  <th>{f.label}</th>
                  <td>
                    <span className="input-unit">
                      <input
                        inputMode="decimal"
                        value={d.per100[f.key]}
                        aria-label={`${f.label}（100g あたり）`}
                        onChange={(e) => set({ per100: { ...d.per100, [f.key]: e.target.value } })}
                      />
                      <span className="unit">{f.unit}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="stack">
            {d.rows.length > 0 && (
              <table className="table recipe-rows">
                <thead>
                  <tr>
                    <th>材料</th>
                    <th className="num">重量</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r, i) => (
                    <tr key={`${r.id}-${i}`}>
                      <td>{r.name}</td>
                      <td className="num">
                        <span className="input-unit">
                          <input
                            inputMode="decimal"
                            value={r.grams}
                            aria-label={`${r.name} の重量`}
                            onChange={(e) => set({ rows: d.rows.map((x, j) => (j === i ? { ...x, grams: e.target.value } : x)) })}
                          />
                          g
                        </span>
                      </td>
                      <td>
                        <button type="button" className="btn-link" onClick={() => set({ rows: d.rows.filter((_, j) => j !== i) })}>
                          外す
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="ingredient-search">
              <input type="search" placeholder="材料を探す（成分表の食品・登録済みの食材と部品）" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="材料を探す" />
              {hits.length > 0 && (
                <ul className="hits">
                  {hits.map((h) => (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => {
                          set({ rows: [...d.rows, { id: h.id, name: h.name, grams: h.typicalUnits[0] ? String(h.typicalUnits[0].grams) : "" }] });
                          setQuery("");
                          setHits([]);
                        }}
                      >
                        <span>{h.name}</span>
                        <span className="muted small">
                          {h.source === "own" ? "★ " : ""}
                          {h.category} ・ 100g {fmtNumber(h.per100g.cal, "kcal")}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <label className="field inline-field">
              <span>仕上がり重量（任意）</span>
              <span className="input-unit">
                <input inputMode="decimal" value={d.finished} onChange={(e) => set({ finished: e.target.value })} aria-label="仕上がり重量" />
                <span className="unit">g</span>
              </span>
              <span className="muted small">炊く・ゆでる・揚げるで重さが変わるとき</span>
            </label>
            {calc && (
              <table className="table kv">
                <caption className="small">100g あたり（保存するとこの値になります）</caption>
                <tbody>
                  {NUTRITION_FIELDS.map((f) => (
                    <tr key={f.key}>
                      <th>{f.label}</th>
                      <td className="num">{calc.per100g[f.key] == null ? "—" : fmtNumber(calc.per100g[f.key], f.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <label className="field">
          <span className="small">補足（任意。規格書の版・日付など）</span>
          <input value={d.basisNote} maxLength={500} onChange={(e) => set({ basisNote: e.target.value })} />
        </label>
      </div>

      <AllergenEditor items={d.allergens} editable candidates={calc?.allergenCandidates ?? {}} onChange={(allergens) => set({ allergens })} />

      {usage && (usage.entries.length > 0 || usage.parts.length > 0) && (
        <div className="notice small">
          <strong>これを使っているもの</strong>（直したら、こちらも計算し直してください）
          <ul>
            {usage.parts.map((p) => (
              <li key={p.id}>部品: {p.name}</li>
            ))}
            {usage.entries.map((e) => (
              <li key={e.id}>
                <Link href={`/entries/${e.id}`}>{e.name}</Link>（{REVIEW_STATE_LABEL[e.reviewState as keyof typeof REVIEW_STATE_LABEL] ?? e.reviewState}）
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      <div className="actions">
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          保存
        </button>
        <span className="spacer" />
        {d.id && (
          <button className="btn danger" disabled={busy} onClick={() => void remove()}>
            削除
          </button>
        )}
      </div>
    </div>
  );
}
